// item 281 — "Launch my environment": a per-user, self-configured set of pages that open
// each in its own browser tab from one Tools click. Stored per-device in localStorage (like
// Appearance, item 61) — a personal preference, not company-wide.

export interface LaunchPage {
  path: string;
  label: string;
}

// the pages a user can put in their launch set (curated from the app's routes)
export const LAUNCHABLE_PAGES: LaunchPage[] = [
  { path: '/', label: 'Dashboard' },
  { path: '/proposals', label: 'Proposals' },
  { path: '/Invoices', label: 'Invoices' },
  { path: '/WorkOrders', label: 'Work Orders' },
  { path: '/PurchaseOrder', label: 'Purchase Orders' },
  { path: '/bills', label: 'Bills' },
  { path: '/customers', label: 'CRM / Customers' },
  { path: '/items', label: 'Items' },
  { path: '/reports', label: 'Reports' },
  { path: '/my-book', label: 'My Book' },
  { path: '/accountant', label: 'Accountant' },
  { path: '/sales-bible', label: 'Sales Bible' },
  { path: '/timesheet', label: 'Timesheet' },
  { path: '/recurring', label: 'Recurring' },
];

const KEY = 'basix.launchSet';

export const getLaunchSet = (): string[] => {
  try {
    const raw = window.localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    // keep only still-valid paths, preserve the saved order
    return Array.isArray(parsed)
      ? parsed.filter((p: unknown) => LAUNCHABLE_PAGES.some((lp) => lp.path === p))
      : [];
  } catch {
    return [];
  }
};

export const saveLaunchSet = (paths: string[]): void => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(paths));
  } catch {
    // ignore quota / privacy-mode failures — the launcher just won't persist
  }
};

// open each configured page in its own tab via window.open (NO features string — a
// 'noopener' feature arg makes Chrome/Brave treat it as a popup; a plain '_blank' opens a
// tab). Deliberately does NOT touch the DOM: an earlier anchor-appendChild/remove approach
// caused a React "removeChild: not a child" crash on re-render. Multiple tabs from one click
// are still gated by the browser's pop-up blocker — the FIRST opens; the rest open once the
// user allows pop-ups for the site (one-time). `blocked` lets the caller surface that hint.
export const launchEnvironment = (paths: string[]): { opened: number; blocked: number } => {
  const { origin } = window.location;
  let opened = 0;
  let blocked = 0;
  paths.forEach((path) => {
    const win = window.open(`${origin}${path}`, '_blank');
    if (win) { opened += 1; win.opener = null; } else { blocked += 1; }
  });
  return { opened, blocked };
};
